import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Text } from 'troika-three-text';
//uuid
import { v4 as uuidv4 } from 'uuid';
//Texture Loading
import { TextureLoader } from 'three';
//import answers
import { answersConfig } from './configure/answersConfig';
import { baseurl } from './app/api/baseurl';


//TEXTURE LOADER CLASS
export class Hdr {

    static load(src) {
        return new Promise(resolve => {
            const loader = new TextureLoader();
            loader.load(src, resolve, undefined, exception => {
                throw exception;
            });
        });
    }
}

//QUESTION - ANSWER ELEMENTS CLASS
export class Pyramide {

    //Requires three-js in environment
    //Requires troika-three-text in environment

    //Reference to creation-index
    pyIndex;
    //Definition of open and closed position of pyramide group
    pyOpenPos = 9;
    pyClosedPos = 6.1;
    pyDelta = this.pyOpenPos - this.pyClosedPos;
    //Is this pyramide group highlighted?
    pyHighlight = false;
    //Materials
    pyMatUpper = new THREE.MeshStandardMaterial({color: 0x4080A0, roughness: 0.2, metalness: 0.6, transparent: true});
    pyMatLower = new THREE.MeshStandardMaterial({color: 0x4080A0, roughness: 0.2, metalness: 0.6, emissive: 0x204080, emissiveIntensity: .1, transparent: true});
    pyMatLine = new THREE.PointsMaterial({color: 0xFFFFFF, size: 0.1});
    //Key Pyramide group that contains all objects
    pyAssembly = new THREE.Group();
    //Pyramide geometry A=upper, B=lower
    pyAGeo = new THREE.ConeGeometry(6,12,4,1,false);
    pyBGeo = new THREE.ConeGeometry(6,12,4,1,false);
    pyAMesh = new THREE.Mesh(this.pyAGeo, this.pyMatUpper);
    pyBMesh = new THREE.Mesh(this.pyBGeo, this.pyMatLower);
    pyALine = new THREE.Points(this.pyAGeo, this.pyMatLine);
    pyBLine = new THREE.Points(this.pyBGeo, this.pyMatLine);
    //Placeholder for textcontents assigned on construction
    pyContent = [];
    //Is Pyramide open and shows text?
    pyOpenState = false;
    //Is Pyramide opening or closing currently?
    pyChangeInProgress = false;
    //Placeholder for troika text element -> needs troika-three-text package
    pyText = new Text();
    pyTextMat = new THREE.MeshBasicMaterial({color: 0xAAAAAA, transparent: true, opacity: 0});
    //Instance UUID
    pyID = '';
    //scoring group
    pyMatScoring = new THREE.MeshStandardMaterial({color: 0x4080A0, roughness: 0.2, metalness: 0.6, transparent: true});
    pyScoring = new THREE.Group();
    pyScoringRegistry = [];
    pyScoringTargets = [];
    pyScoringTexts = [];
    //scoring element
    //pyScoringGeo = new THREE.BoxGeometry(.5,.5,.5);
    pyScoringGeo = new THREE.OctahedronGeometry(0.5, 0);
    pyScore;
    pyHasScore = false;
    pyScoringOutlineMaterial = new THREE.PointsMaterial({color: 0xFFFFFF, size: 0.04});
    pyScoringStaysExposed = false;
    //Simulate an Addition
    pyAddition = false;
    pyAdditionColor;
    pyAdditionNumber;
    pyAdditionObject;
    pyAdditionMaterial = new THREE.MeshStandardMaterial({color: 0x4080A0, roughness: 0.2, metalness: 0.6, emissive: 0x204080, emissiveIntensity: .6, transparent: true});
    pyAnimation = 2;

    constructor(cnt, scr, ind) {
        this.pyIndex = ind;
        this.pyID = uuidv4(); //to reverse-track clicks from meshes to class instance we give uuid name to meshes
        this.pyAssembly.name = this.pyID;
        this.pyAMesh.name = this.pyBMesh.name = this.pyID;
        this.pyContent = cnt;
        this.pyAssembly.add(this.pyAMesh, this.pyBMesh, this.pyALine, this.pyBLine);
        this.pyBMesh.rotateZ(Math.PI);
        this.pyBLine.rotateZ(Math.PI);

        this.pyText.text = this.pyContent[0];
        this.pyText.font = '../MavenPro-Regular.ttf';
        if (this.pyText.text.length > 50) {
          this.pyText.fontSize = 1.5;
        } else {
          this.pyText.fontSize = 2.2;
        }
        this.pyText.anchorX = 'center';
        this.pyText.anchorY = 'middle';
        this.pyText.material = this.pyTextMat;
        this.pyText.maxWidth = 28;
        this.pyText.textAlign = 'center';
        this.pyText.name = 'text';
        this.pyText.sync();
        this.pyAssembly.add (this.pyText);

        this.pyAMesh.position.y = this.pyALine.position.y = this.pyClosedPos; 
        this.pyBMesh.position.y = this.pyBLine.position.y = -this.pyClosedPos;

        this.pyScore = scr;

        this.pyHighlight = false;
        }

    buildScore (radius, camera) {
        if (this.pyScore.length > 0) {
            const radian_interval = (2 * Math.PI) / this.pyScore.length;
            this.pyScoring.clear(); //remove everything from existing THREE groupe for scoring
            this.pyScoringRegistry = []; //remove everything from the existing Scoring registry array
            this.pyScoringTargets = []; //remove everything from existing mouse target array
            this.pyScoringTexts = [];
            for (let index = 0; index < this.pyScore.length; index++) { //create n groups of instance objects

                this.pyScoringRegistry.push(new THREE.InstancedMesh(this.pyScoringGeo,this.pyMatScoring,this.pyScore[index]));
                this.pyScoring.add(this.pyScoringRegistry[index]);
                let colorInfluencer = Math.random();

                for (let i = 0; i < this.pyScore[index]; i++) {
                    //Position first on orbit
                    let arrangingDummy = new THREE.Object3D(); //Dummy to make changes to instanced meshes
                    arrangingDummy.position.set(
                        (Math.cos(radian_interval * index) * radius),
                        0,
                        (Math.sin(radian_interval * index) * radius)
                    );
                    //spherical distribution
                    let ranPos = new THREE.Vector3().randomDirection();
                    let distMulti = Math.random()*Math.cbrt((this.pyScore[index]*3)/(4*Math.PI)); //random value of a radius in a sphere with the volume this.score[index] -> sphere grows with more elements
                    
                    arrangingDummy.position.x = arrangingDummy.position.x + ranPos.x * distMulti;
                    arrangingDummy.position.y = arrangingDummy.position.y + ranPos.y * distMulti;
                    arrangingDummy.position.z = arrangingDummy.position.z + ranPos.z * distMulti;

                    let ranRot = Math.random()*Math.PI*2;
                    arrangingDummy.rotateX(ranRot);
                    arrangingDummy.rotateY(ranRot);
        
                    arrangingDummy.updateMatrix();
                    this.pyScoringRegistry[index].setMatrixAt(i, arrangingDummy.matrix);
                    this.pyScoringRegistry[index].setColorAt(i, new THREE.Color(colorInfluencer*0xFFFFFF));
                }

                //add extra cube if this object is under simulate Addition status
                if (this.pyAddition && index === this.pyAdditionNumber) {
                    this.pyAdditionObject = new THREE.Mesh(this.pyScoringGeo, this.pyAdditionMaterial);
                    this.pyAdditionObject.material.color = new THREE.Color(this.pyAdditionColor);
                    this.pyAdditionObject.material.emissive = new THREE.Color(this.pyAdditionColor);
                    this.pyScoring.add(this.pyAdditionObject);
                    this.pyAdditionObject.scale.set(20,20,20);
                    this.pyAdditionObject.position.set(
                      camera.position.x,
                      camera.position.y,
                      camera.position.z
                    );
                    this.pyAdditionObject.material.opacity = 0;
                }

                //make bounding sphere
                let bSphereGeo = new THREE.IcosahedronGeometry(Math.cbrt((this.pyScore[index]*3)/(4*Math.PI)), 3);
                let bSphereMesh = new THREE.Points(bSphereGeo, new THREE.PointsMaterial({color: 0xFFFFFF, size: 0.04}));
                bSphereMesh.name = this.pyContent[index+1];
                this.pyScoring.add(bSphereMesh);
                this.pyScoringTargets.push(bSphereMesh);
                bSphereMesh.position.set(
                    (Math.cos(radian_interval * index) * radius),
                    0,
                    (Math.sin(radian_interval * index) * radius)
                );
                let bScoreTextUp = new Text();
                let bScoreTextLow = new Text();
                bScoreTextUp.text = this.pyContent[index+1];
                bScoreTextLow.text = this.pyScore[index];
                bScoreTextUp.fontSize = 0.8;
                bScoreTextLow.fontSize = 1.0;
                bScoreTextLow.font = bScoreTextUp.font = '../MavenPro-Regular.ttf';
                bScoreTextUp.anchorX = bScoreTextLow.anchorX = 'center';
                bScoreTextUp.anchorY = bScoreTextLow.anchorY = 'middle';
                bScoreTextUp.textAlign = bScoreTextLow.textAlign = 'center';
                bScoreTextUp.material = bScoreTextLow.material = this.pyTextMat;
                bScoreTextUp.maxWidth = 12;
                bScoreTextUp.whiteSpace = 'normal';
                bScoreTextUp.sync();
                bScoreTextLow.sync();
                let textGroup = new THREE.Group();
                textGroup.add(bScoreTextUp, bScoreTextLow);
                textGroup.name = this.pyContent[index+1];
                this.pyScoring.add(textGroup);
                this.pyScoringTexts.push(textGroup);
                textGroup.position.set(
                    (Math.cos(radian_interval * index) * radius),
                    0,
                    (Math.sin(radian_interval * index) * radius)
                );
                bScoreTextUp.position.set(
                    0,
                    (Math.cbrt((this.pyScore[index]*3)/(4*Math.PI))+1),
                    0
                );
                bScoreTextLow.position.set(
                    0,
                    -(Math.cbrt((this.pyScore[index]*3)/(4*Math.PI))+1),
                    0
                );
                textGroup.visible = false;
            }
            this.pyHasScore = true;   
        }
    }

    makeClose () {
        if(this.pyAMesh.position.y > this.pyClosedPos) {
            this.pyChangeInProgress = true;
            this.pyAMesh.position.y -= this.pyDelta/100;
            this.pyBMesh.position.y += this.pyDelta/100;
            this.pyALine.position.y -= this.pyDelta/100;
            this.pyBLine.position.y += this.pyDelta/100;
            this.pyTextMat.opacity -= 0.01; 
            this.pyText.sync();
            //Destroy score orbit
            if (this.pyHasScore) {
                this.pyScoring.clear(); //flush the scoring group  
                this.pyHasScore = false;
            }
            this.pyScoringStaysExposed = false;
            this.pyAddition = false;
        } else {
            this.pyOpenState = false;
            this.pyChangeInProgress = false;
        }
    }

    makeOpen (camera) {
        if(this.pyAMesh.position.y < this.pyOpenPos) {
            this.pyChangeInProgress = true;
            this.pyAMesh.position.y += this.pyDelta/100;
            this.pyBMesh.position.y -= this.pyDelta/100;
            this.pyALine.position.y += this.pyDelta/100;
            this.pyBLine.position.y -= this.pyDelta/100;
            this.pyTextMat.opacity += 0.01; 
            this.pyText.sync();
            //Make a score orbit
            if (!this.pyHasScore) {
                this.buildScore(18, camera); //radius of 8
                this.pyAssembly.add(this.pyScoring); //add the new scoring group to the assembly object
            }
        } else {
            this.pyOpenState = true;
            this.pyChangeInProgress = false;
        }
        
    }

    //call to animate all rotation and color features
    animate (time, factor, color, cam) {
        //rotate systems
        this.pyAMesh.rotation.y = this.pyALine.rotation.y = time/factor; 
        this.pyBMesh.rotation.y = this.pyBLine.rotation.y = time/factor;
        this.pyScoring.rotation.y = time/factor*2;
        //rotate instances in case that this pyramide shows a Score
        if (this.pyHasScore) {
            const matrix = new THREE.Matrix4(); // for instance animation
            let dummy = new THREE.Object3D();

            //influence transparency of upper element
            if (cam.position.y > 0) { //only if value positive -> camera higher
              this.pyMatUpper.opacity = 1/cam.position.y*4;
            }
            if (cam.position.y > 10) { //only if value positive -> camera higher
              for (let index = 0; index < this.pyScoringTexts.length; index++) {
                this.pyScoringTexts[index].visible = true;  
                this.pyScoringTargets[index].material.size = 0.1;
              }
            }

            if (this.pyScoringStaysExposed) {
              this.pyScoringTargets[this.pyAdditionNumber].material.size = 0.1;
              this.pyScoringTexts[this.pyAdditionNumber].visible = true;                 
            }

            for (let c = 0; c < this.pyScore.length; c++) {
                for (let i = 0; i < this.pyScore[c]; i++) {
                
                    this.pyScoringRegistry[c].getMatrixAt(i, matrix);
                    matrix.decompose(dummy.position, dummy.rotation, dummy.scale);
        
                    dummy.rotation.x = Math.sin(i*.73) * time/1000;
                    dummy.rotation.y = Math.sin(i*.73) * time/500;
                    dummy.rotation.z = Math.sin(i*.73) * time/1200;
                
                    dummy.updateMatrix();
                    this.pyScoringRegistry[c].setMatrixAt(i, dummy.matrix);
                }
        
                this.pyScoringRegistry[c].instanceMatrix.needsUpdate = true;
                
                //return Score blobs to normal
                this.pyScoringTexts[c].lookAt(cam.position);
                if (!this.pyScoringStaysExposed && cam.position.y < 10) {
                  this.pyScoringTargets[c].material.size = 0.04;
                  this.pyScoringTexts[c].visible = false;
                }
            }

            if (this.pyAddition) {
              //const radian_interval = (2 * Math.PI) / this.pyScore.length;
              this.pyAdditionObject.rotation.x = time/1000;
              this.pyAdditionObject.rotation.y = time/500;
              this.pyAdditionObject.rotation.z = time/1200;
              if (this.pyAdditionObject.material.opacity < 1) {
                this.pyAdditionObject.material.opacity += 0.01;
              }
              if (this.pyAnimation > 0.2) {
                //Animate factor
                this.pyAnimation -= 0.005;
                //Animate size by factor
                this.pyAdditionObject.scale.x = 10 * this.pyAnimation;
                this.pyAdditionObject.scale.y = 10 * this.pyAnimation;
                this.pyAdditionObject.scale.z = 10 * this.pyAnimation;
                //Move towards pyScoringTarget position
                this.pyAdditionObject.position.x = ((this.pyAdditionObject.position.x * 19)+this.pyScoringTargets[this.pyAdditionNumber].position.x)/20;
                this.pyAdditionObject.position.y = ((this.pyAdditionObject.position.y * 19)+this.pyScoringTargets[this.pyAdditionNumber].position.y)/20;
                this.pyAdditionObject.position.z = ((this.pyAdditionObject.position.z * 19)+this.pyScoringTargets[this.pyAdditionNumber].position.z)/20;
              } 
            }
        } else {
          //upper part not transparent
          this.pyMatUpper.opacity = 1;
        }
        //color adaptation
        if (!this.pyHighlight) {
            this.pyMatUpper.color = color;
            this.pyMatLower.color = color;
            this.pyMatLower.emissive = color;
            this.pyMatLower.emissiveIntensity = 0.1;
        } else {
            this.pyMatUpper.color = color;
            this.pyMatLower.color = color;
            this.pyMatLower.emissive = new THREE.Color({color: 0xFFFFFF});
            this.pyMatLower.emissiveIntensity = 0.8;
        }
        this.pyAssembly.getObjectByName('text').lookAt(cam.position);
    }

    //call to check distance to camera and initiate makeOpen / makeClose calls
    checkDistance (cam, dist) {
        var distance = cam.position.distanceTo( this.pyAssembly.position );
        if ((distance < dist && !this.pyOpenState) || (this.pyChangeInProgress && !this.pyOpenState)) {
            this.makeOpen(cam);
        } else if ((distance > dist && this.pyOpenState) || (this.pyChangeInProgress && this.pyOpenState)) {
            this.makeClose();
        }
    }

    highlight () {
        this.pyHighlight = true;
    }

    lowlight () {
        this.pyHighlight = false;
    }

    exposeScoreBlob (name) {
      //when called with a string
      if (isNaN(name)) {
        //this.pyScoringTargets.getObjectByName('name').material.size = 0.1;
        var result = this.pyScoringTargets.filter(obj => {
            return obj.name === name
          });
        if (result.length >0) {
            result[0].material.size = 0.1;
        }
        var resulu = this.pyScoringTexts.filter(obj => {
            return obj.name === name
          });
        if (resulu.length >0) {
            resulu[0].visible = true;
            //OLD
            //resulu[1].visible = true;
        }
      } else if (!isNaN(name) && name != null) { //when called with a number
        for (let index = 0; index < this.pyScoringTargets.length; index++) {
          this.pyScoringTargets[index].material.size = 0.1;
        }
        for (let index = 0; index < this.pyScoringTexts.length; index++) {
          this.pyScoringTexts[index].visible = true;
        }
      }
    }

    updateScore(scr) {
      this.pyScore = scr;
    }

    simulateAddition(obj) {
      this.pyAddition = true;
      this.pyAdditionColor = `#${obj.color}`;
      this.pyAdditionNumber = obj.answers[this.pyIndex];
      this.pyAnimation = 2;
      this.pyScoringStaysExposed = true;
    }

    overWrite(obj, camera, scene) {
      this.pyAddition = true;
      this.pyAdditionColor = `#${obj.color}`;
      this.pyAdditionNumber = obj.answers[this.pyIndex];
      this.pyAnimation = 2;
      
      //clean up
      var deletions = this.pyScoring.getObjectsByProperty('name', 'usercube');
      deletions.forEach(element => {
        element.removeFromParent();
      });

      //generate additional Object
      this.pyAdditionObject = new THREE.Mesh(this.pyScoringGeo, this.pyAdditionMaterial);
      this.pyAdditionObject.material.color = new THREE.Color(this.pyAdditionColor);
      this.pyAdditionObject.material.emissive = new THREE.Color(this.pyAdditionColor);
      this.pyAdditionObject.name = 'usercube';
      this.pyScoring.add(this.pyAdditionObject);
      this.pyAdditionObject.scale.set(20,20,20);
      this.pyAdditionObject.position.set(
        camera.position.x,
        camera.position.y,
        camera.position.z
      );
      this.pyAdditionObject.material.opacity = 0;

      this.pyScoringStaysExposed = true;
    }

}

//ENVIRONMENT STARFIELD CLASS
export class Stars {

    starfield = new THREE.Group();
    generatedStars = 0;

    constructor(minRad, maxRad, amount) {
        //Generate Star Instances
        const starGeo = new THREE.OctahedronGeometry(1,0);
        const starMat = new THREE.MeshStandardMaterial({metalness: 0.8, roughness: 0.6});
        const starfieldCollector = new THREE.InstancedMesh(starGeo, starMat, amount);
        starfieldCollector.name = 'starfieldCollector';
        //Position Star Instances
        const starDummy = new THREE.Object3D();
        for (let i = 0; i < amount; i++) {
            //spherical distribution
            let ranPos = new THREE.Vector3().randomDirection();
            let distMulti = maxRad;
            let distFactor = (Math.random() * (1-(minRad/maxRad))) + (minRad/maxRad);
            //let distMulti = Math.random() * 5; //randomized distance
            starDummy.position.x = distFactor * ranPos.x * distMulti;
            starDummy.position.y = distFactor * ranPos.y * distMulti;
            starDummy.position.z = distFactor * ranPos.z * distMulti;
            starDummy.scale.x = starDummy.scale.y = starDummy.scale.z = 40;

            starDummy.updateMatrix();
            starfieldCollector.setMatrixAt(i, starDummy.matrix);
            starfieldCollector.setColorAt(i, new THREE.Color(Math.random() * 0xFFFFFF));
        }
        this.starfield.add(starfieldCollector);
        this.generatedStars = amount;
    }

    animate (color) {
        for (let i = 0; i < this.generatedStars; i++) {
            this.starfield.getObjectByName('starfieldCollector').setColorAt(i, new THREE.Color(color));
        }
    }
}

//CAMERA TRAVEL CLASS
export class Travel {
    trSteps = [];
    trCamSteps = [];
    trCounter = 0;
    trObjectLastTraveled;
    trStepsCount;
    trDistance = 16;

    constructor (cam, obj, id, controls) {
        let steps = Math.abs(Math.round(cam.position.distanceTo(obj.position)/2));
        if (steps > (this.trDistance+2)) {
            for (let index = 0; index < steps-this.trDistance; index++) {
                let dummy = new THREE.Vector3();
                dummy.x = cam.position.x + ((obj.position.x - cam.position.x) / steps * index);
                dummy.y = cam.position.y + ((obj.position.y - cam.position.y) / steps * index);
                dummy.z = cam.position.z + ((obj.position.z - cam.position.z) / steps * index);
                this.trSteps.push(dummy);
            }
            for (let index = 0; index < steps-this.trDistance; index++) {
                let dummy = new THREE.Vector3();
                dummy.x = controls.target.x + ((obj.position.x - controls.target.x) / (steps-this.trDistance) * index);
                dummy.y = controls.target.y + ((obj.position.y - controls.target.y) / (steps-this.trDistance) * index);
                dummy.z = controls.target.z + ((obj.position.z - controls.target.z) / (steps-this.trDistance) * index);
                this.trCamSteps.push(dummy);
            }
            this.trObjectLastTraveled = id;
            this.trStepsCount = steps;
        } else {
            this.trStepsCount = -1;
        }
    }

    travel (cam, controls) {
        cam.position.set(this.trSteps[this.trCounter].x, this.trSteps[this.trCounter].y, this.trSteps[this.trCounter].z);
        controls.target = this.trCamSteps[this.trCounter];
        if (this.trCounter >= this.trSteps.length-1) {
            return 1;
        } else {
            this.trCounter += 1;
            return 0;
        }
    }
}

//DATA AKQUISITION CLASS
export class DataAkquisition {

  questionsAndAnswers = answersConfig; //Read in questions and answers from config file
  questionsAndAnswersValues = []; //Prep the array to hold statistics
  dataSet = []; //temporary dataset with all answers
  focusSet = []; //dataset with focus elements
  focusNewest = ''; //memorize newest update data of current focus sets
  hasData = false; //?
  hasUpdate = false; //signals to dataObjects that they should update data from statistics
  infoDiv; //UI Info Container
  colorDiv; //UI Color Container

  constructor (infoDiv, colorDiv) {
    //creating empty array
    for (let index = 0; index < this.questionsAndAnswers.length; index++) {
      var dummy = new Array(this.questionsAndAnswers[index].n).fill(0);
      this.questionsAndAnswersValues.push(dummy);
    }
    this.infoDiv = infoDiv;
    this.colorDiv = colorDiv;
    this.queryData(); //trigger initial data fetching
  }

  async queryData (params) {
    //get Data and fill to the array
      await fetch(baseurl+'/answers/')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error:${response.status}`);
        }
        return response.json();
      })
      .then( json => json.slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))) //already sort the datasets here
      .then( data => this.dataSet = data)
      .catch((err) => console.error(`Fetch problem: ${err.message} on API ${baseurl}/answers/`))
  }

  processData() {
    let foundNew = false;
    let updateDataSet = [];
    for (let index = 0; index < this.dataSet.length; index++) {
      if (this.dataSet[index].updatedAt > this.focusNewest) {
        //if there is a new data set, push it
        updateDataSet.push(this.dataSet[index]);
        foundNew = true;
      } else {
        //if there are no new data sets
        break;
      }
    }
    if (foundNew) {
      this.makeStatistics(); //calculate new statistics
      this.updateFocus(updateDataSet); //update the focus set with updateDataSet
      this.infoDiv.insertAdjacentHTML('beforeEnd', `<p>Received / Updated ${updateDataSet.length} data set(s). Newest from ${this.focusNewest}.</p>`);
      this.infoDiv.scrollTop = this.infoDiv.scrollHeight; //scroll down in the infoDiv
      foundNew = false; //reset the foundNew toggle
    } 
    this.dataSet = [] //destroy latest dataSet, not needed any more
  }

  makeStatistics() {
    if (this.dataSet !== null && this.dataSet.length > 0) {
      //cleaning up
      for (let index = 0; index < this.questionsAndAnswersValues.length; index++) {
        this.questionsAndAnswersValues[index].fill(0);
      }
      //refilling
      for (let index = 0; index < this.dataSet.length; index++) {
        for (let j = 0; j < this.questionsAndAnswers.length; j++) {
          //test if value NOT -1 or would result in index larger than target array length
          if (this.dataSet[index].answers[j] !== -1 && this.dataSet[index].answers[j] < this.questionsAndAnswersValues[j].length) {
            //then add a vote to the respective answer
            this.questionsAndAnswersValues[j][this.dataSet[index].answers[j]] += 1;
          } 
        }
      }
      this.hasUpdate = true;
    }
  }

  updateFocus (updateData) {
    if (this.focusNewest !== '') { //just copy data sets when starting up
      for (let index = 0; index < updateData.length; index++) {
        if (this.focusSet[0] && updateData[updateData.length-(index+1)]._id === this.focusSet[0]._id) { //if there is only a change in value for same id
          this.focusSet[0] = updateData[updateData.length-(index+1)] //replace the latest focusSet with new value
        } else { //but if different id
          this.focusSet.splice(0, 0, updateData[updateData.length-(index+1)]); //then splice the updated data set in at index 0
        }
      }
    } else { //if starting up only set focusNewest to newest Update time and copy
      this.focusSet = updateData;
    }
    //update focusNewest
    this.focusNewest = this.focusSet[0].updatedAt;
    //set Color of ColorDiv to latest focusSet lead
    this.colorDiv.style.backgroundColor = `#${this.focusSet[0].color}`;
  }

  hasFocus () {
    let focusId = -1;
    if (this.focusSet !== null && this.focusSet.length > 0) {
      const dateObj = new Date();
      let timeNow = dateObj.getTime()
      let initialLength = this.focusSet.length;
      for (let j = 1; j <= initialLength; j++) {
        let deltaTime = timeNow - Date.parse(this.focusSet[initialLength-j].updatedAt);
        if (deltaTime > 30000) { //delete focusSet element if older than 30 secs
          this.focusSet.splice(initialLength-j, 1);
        } else {
          focusId = initialLength-j;
          break;
        }
      }
    }
    if (focusId !== -1) {
      let transferObject = this.focusSet[focusId];
      this.focusSet.splice(focusId,1); // dreictly delete the transferred focusSet element.
      return (transferObject);
    } else {
      return null;
    }
  }

}

export class Introduction {

  startTime; //time of construction
  eventTime = 0; //time of last interaction
  introEntry; //introInfoButton
  introPlane;
  introConnector;
  introContent;
  introNext;
  introTimer; //interval for checking
  introAnimated = true; //is the i button having a bg color animation?
  state = 0;

  constructor(document) {
    this.startTime = Date.now();
    
    // Build the intro Button
    this.introEntry = document.createElement('div');
    this.introEntry.classList.add('introEntry');
    this.introEntry.classList.add('introEntryAnimation');
    this.introEntry.id = 'iBut';
    this.introEntry.innerHTML = 'i';
    document.body.appendChild(this.introEntry);
    
    // Build the connector
    this.introConnector = document.createElement('div');
    this.introConnector.classList.add('introConnector');
    document.body.appendChild(this.introConnector);

    // Build the info plane
    this.introPlane = document.createElement('div');
    this.introPlane.classList.add('introPlane');
    this.introPlane.innerHTML = `<div id='iKill' class='introKill'></div><div id='introContent'></div><div id='iNext' class='introNext'></div>`;
    document.body.appendChild(this.introPlane);
    this.introContent = document.getElementById('introContent');
    this.introNext = document.getElementById('iNext');

  }

  click () {
    switch (this.state) {
      case 0:
        if (this.introEntry.classList.contains('introEntryAnimation')) {
          this.introEntry.classList.remove('introEntryAnimation');
        }
        this.eventTime = Date.now();
        this.introNext.style.opacity = '1';
        this.introPlane.style.right = '15px';
        this.introPlane.style.bottom = '120px';
        this.introContent.innerHTML = `<p><b>4 quick steps to get you acquainted with our poll site:</b></p><p>1. Scan the QR code to access the questionnaire.</p>`;
        this.introPlane.style.opacity = '1';
        this.introConnector.style.width = '110px';
        this.introConnector.style.height = '115px';
        this.introConnector.style.right = '115px';
        this.introConnector.style.bottom= '5px';
        this.introConnector.style.opacity = '1';
        this.state = 1;
        break;

      case 1:
        this.eventTime = Date.now();
        this.introPlane.style.right = '75px';
        this.introPlane.style.bottom = '120px';
        this.introContent.innerHTML = `<p>2. Choose a color in the questionnaire that identifies you and your answers.</p>`;
        this.introConnector.style.width = '60px';
        this.introConnector.style.height = '115px';
        this.introConnector.style.right = '225px';
        this.introConnector.style.bottom= '5px';
        this.state = 2;
        break;

      case 2:
        this.eventTime = Date.now();
        this.introPlane.style.right = '40%';
        this.introPlane.style.bottom = '30%';
        //this.introPlane.style.height = '25vb';
        this.introContent.innerHTML = `<p>3. The camera will pick events to focus on – if you’re lucky and not too many people contribute at the same time, you will see data-cubes in your color being added to the result clusters. To force this, contribute to a question that is just visible on screen.</p>`;
        this.introConnector.style.opacity = '0';
        this.state = 3;
        break;

      case 3:
        this.eventTime = Date.now();
        this.introPlane.style.right = '40%';
        this.introPlane.style.bottom = '30%';
        //this.introPlane.style.height = '25vb';
        this.introNext.style.opacity = '0';
        this.introContent.innerHTML = `<p>4. Use your mouse to explore the data – you can look from the top to see all the results of one question in one view. Once you don’t interact anymore, the camera will resume auto-travelling.</p><p>Enjoy!</p>`;
        this.state = 4;
        break;

      case 4:
        this.eventTime = 0;
        this.introConnector.style.opacity = '0';
        this.introPlane.style.opacity = '0';
        this.introContent.innerHTML = ``;
        this.state = 0;
        break;
    
      default:
        break;
    }
  }

  kill () {
    this.eventTime = 0;
    this.state = 0;
    this.introConnector.style.opacity = '0';
    this.introPlane.style.opacity = '0';
  }

  checkTimer () {
    const deltaTime = Date.now();
    //kill Button Animation after 60 secs
    if (this.introAnimated && (deltaTime - this.startTime) > 60000) {
      this.introAnimated = false;
      this.introEntry.classList.remove('introEntryAnimation');
    }
    if (this.eventTime !== 0 && (deltaTime - this.eventTime) > 20000) {
      this.kill();
    }
  }

}

//Utils and shaders for waveform
const getDefaultUniforms = () => {
    return {
        u_time: { value: 0.0 },
        u_mouse: {
            value: {
                x: 0.0,
                y: 0.0
            }
        },
        u_resolution: {
            value: {
                x: window.innerWidth * window.devicePixelRatio,
                y: window.innerHeight * window.devicePixelRatio
            }
        }
    }
}
const uniforms = {
    ...getDefaultUniforms(),
    u_pointsize: { value: 2.0 },
    // wave 1
    u_noise_freq_1: { value: 0.04 },
    u_noise_amp_1: { value: 8 },
    u_spd_modifier_1: { value: 0.2 },
    // wave 2
    u_noise_freq_2: { value: 0.04 },
    u_noise_amp_2: { value: 8 },
    u_spd_modifier_2: { value: 0.2 },
    //fragment shader  timer
    u_time_frag: {value: 0.0}
};
function vertexshader() {
    return `
        #define PI 3.14159265359

        uniform float u_time;
        uniform float u_pointsize;
        uniform float u_noise_amp_1;
        uniform float u_noise_freq_1;
        uniform float u_spd_modifier_1;
        uniform float u_noise_amp_2;
        uniform float u_noise_freq_2;
        uniform float u_spd_modifier_2;

        // 2D Random
        float random (in vec2 st) {
            return fract(sin(dot(st.xy,
                                vec2(12.9898,78.233)))
                        * 43758.5453123);
        }

        // 2D Noise based on Morgan McGuire @morgan3d
        // https://www.shadertoy.com/view/4dS3Wd
        float noise (in vec2 st) {
            vec2 i = floor(st);
            vec2 f = fract(st);

            // Four corners in 2D of a tile
            float a = random(i);
            float b = random(i + vec2(1.0, 0.0));
            float c = random(i + vec2(0.0, 1.0));
            float d = random(i + vec2(1.0, 1.0));

            // Smooth Interpolation

            // Cubic Hermine Curve.  Same as SmoothStep()
            vec2 u = f*f*(3.0-2.0*f);
            // u = smoothstep(0.,1.,f);

            // Mix 4 coorners percentages
            return mix(a, b, u.x) +
                    (c - a)* u.y * (1.0 - u.x) +
                    (d - b) * u.x * u.y;
        }

        mat2 rotate2d(float angle){
            return mat2(cos(angle),-sin(angle),
                    sin(angle),cos(angle));
        }

        void main() {
            gl_PointSize = u_pointsize;
            vec3 pos = position;
            // pos.xy is the original 2D dimension of the plane coordinates
            pos.z += noise(pos.xy * u_noise_freq_1 + u_time * u_spd_modifier_1) * u_noise_amp_1;
            // add noise layering
            // minus u_time makes the second layer of wave goes the other direction
            pos.z += noise(rotate2d(PI / 4.) * pos.yx * u_noise_freq_2 - u_time * u_spd_modifier_2 * 0.6) * u_noise_amp_2;
            vec4 mvm = modelViewMatrix * vec4(pos, 1.0);
            gl_Position = projectionMatrix * mvm;
          }`;
}
function fragmentshader() {
    return`
    #ifdef GL_ES
    precision mediump float;
    #endif
    #define PI 3.14159265359
    #define TWO_PI 6.28318530718
    
    uniform vec2 u_resolution;
    uniform float u_time_frag;
    void main() {
      vec2 st = gl_FragCoord.xy/u_resolution.xy;
      gl_FragColor = vec4(abs(sin(u_time_frag))/4.0, st,1.0);
    }
    `;
}

//CLICK FINDER
//returns array id of element or -1 for no intersection
function findIntersections(mouse, camera, dataObjects, dataObjectsAmount, scene) {

    var raycaster = new THREE.Raycaster();
    let id = -1;

    // create a Ray with origin at the mouse position
    //   and direction into the scene (camera direction)
    raycaster.setFromCamera( mouse, camera );

    // create an array containing all objects in the scene with which the ray intersects
    for (let index = 0; index < dataObjectsAmount; index++) {
        var intersects = raycaster.intersectObjects( scene.getObjectsByProperty('name',dataObjects[index].pyID), false );       
        //if (intersects.length > 0 && INTERSECTED.id != intersects[0].object.id) {
        if (intersects.length > 0) {
            id = index;
        };
    }

    return id;
}

//MOUSEOVER FINDER
//returns array id of element or -1 for no intersection
function findIntersectionsHover(mouse, camera, dataObjects, dataObjectsAmount, scene) {

    var raycaster = new THREE.Raycaster();
    let id = [-1, 0];

    // create a Ray with origin at the mouse position
    //   and direction into the scene (camera direction)
    raycaster.setFromCamera( mouse, camera );

    // create an array containing all objects in the scene with which the ray intersects
    for (let index = 0; index < dataObjectsAmount; index++) {
        var intersects = raycaster.intersectObjects( dataObjects[index].pyScoringTargets, true );
        if (intersects.length > 0) {
            id = [index, intersects[0].object.name, 'blob'];
        } else {
          var intersects2 = raycaster.intersectObjects( scene.getObjectsByProperty('name',dataObjects[index].pyID), false );
          if (intersects2.length > 0) {
            id = [index, intersects2[0].object.name, 'py'];
          }
        };
    }

    return id;
}

const Visualizer = () => {
  
  //////////
  //
  // Build Subset
  //
  //////////

  document.body.innerHTML = '';
  document.body.style.backgroundColor = '#050505';
  
  //Logo
  var logoDiv = document.createElement("div");
  logoDiv.classList.add('visLogo');
  document.body.appendChild(logoDiv);

  //QR Code
  var QRDiv = document.createElement("div");
  QRDiv.classList.add('visQR');
  //PRODUCTION (now in CSS)
  //let qrUrl = `url('${baseurl}/qr/qrLink.svg')`;
  document.body.appendChild(QRDiv);

  //ColorBox
  var colorDiv = document.createElement("div");
  colorDiv.classList.add('visColor');
  document.body.appendChild(colorDiv);

  //Info -> only show where enough space
  
    var infoDiv = document.createElement("div");
    infoDiv.classList.add('visInfo');
    document.body.appendChild(infoDiv);
    if (window.innerWidth/2*3 > window.innerHeight) {
      infoDiv.style.display = 'block';
    } else {
      infoDiv.style.display = 'none';
    }

  //pyText
  var pyDiv = document.createElement("div");
  pyDiv.classList.add('visPy');
  pyDiv.style.visibility = 'hidden';
  document.body.appendChild(pyDiv);

  //Intro
  const introduction = new Introduction(document);
  var infoButton = document.getElementById('iBut');
  infoButton.addEventListener('click', function() {
    introduction.click();
  }, false);
  var infoNext = document.getElementById('iNext');
  infoNext.addEventListener('click', function() {
    introduction.click();
  }, false);
  var infoKill = document.getElementById('iKill');
  infoKill.addEventListener('click', function() {
    introduction.kill();
  }, false);


  ///////////
  //
  // INIT
  //
  ///////////

  //create timer element for self animation
  var timerSet = Date.now();
  var timerData = timerSet;
  var lastPyramide = 0;
  var globalMouseEvent; //copy for mouse move events
  var globalMouseEventHappened = false; //avoid that empty mouse event is called at startup before mouse moves once ...
  var pyHover = -1; //Mark what dataObject mouse is hovering over

  //Get Data Sets and wait for exec
  //Hand over infoDiv, so dataAq can leave some messages.
  const dataAq = new DataAkquisition(infoDiv, colorDiv);
  infoDiv.innerHTML = `<p> Loading and Preparing Data Sets ... </p>`;
  var updateTimer = setInterval(() => {
    checkInit()}, 500);

  function checkInit() {

    //see if condition to run is there
    if (dataAq.dataSet != null && dataAq.dataSet.length > 0) {
      clearInterval(updateTimer);
    } else {
    return;
    }

    //THREE create camera and scene
    const camera = new THREE.PerspectiveCamera( 70, window.innerWidth / window.innerHeight, 0.01, 4000 );
    camera.position.z = 40;
    const scene = new THREE.Scene();
    //scene.fog = new THREE.Fog(0x202080, 20, 40); //fog doesn't influence wave fragment shader -> not usable

    //create Pyramid objects
    let dataObjects = [];
    let dataObjectsNames = [];
    const dataObjectsAmount = dataAq.questionsAndAnswers.length; //number of questions in text array
    for (let index = 0; index < dataObjectsAmount; index++) {
        let hasNoCloseNeighbour = false;
        // compose text input
        let textElements = [];
        textElements.push(dataAq.questionsAndAnswers[index].question);
        textElements.push(dataAq.questionsAndAnswers[index].a1? dataAq.questionsAndAnswers[index].a1 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a2? dataAq.questionsAndAnswers[index].a2 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a3? dataAq.questionsAndAnswers[index].a3 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a4? dataAq.questionsAndAnswers[index].a4 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a5? dataAq.questionsAndAnswers[index].a5 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a6? dataAq.questionsAndAnswers[index].a6 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a7? dataAq.questionsAndAnswers[index].a7 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a8? dataAq.questionsAndAnswers[index].a8 : null);
        textElements.push(dataAq.questionsAndAnswers[index].a9? dataAq.questionsAndAnswers[index].a9 : null);
        //create Object
        let pyramide = new Pyramide(textElements, dataAq.questionsAndAnswersValues[index], index);
        dataObjects.push(pyramide);
        dataObjectsNames.push(pyramide.pyID);
        scene.add(dataObjects[index].pyAssembly);
        //create random position while checking not to be too close to neighbour pyramides
        while (!hasNoCloseNeighbour && index > 0) {
            dataObjects[index].pyAssembly.position.x = (Math.random() * 500)-250;
            dataObjects[index].pyAssembly.position.z = (Math.random() * 500)-250;
            hasNoCloseNeighbour = true;
            for (let c = 0; c < index; c++) {
                if (dataObjects[index].pyAssembly.position.distanceTo(dataObjects[c].pyAssembly.position) < 80) {
                    hasNoCloseNeighbour = false;
                }
                
            }  
        }
      
    }

    infoDiv.insertAdjacentHTML('beforeEnd', `<p> ${dataObjectsAmount} data objects created. </p>`);

    //ENVIRONMENT load Environment texture
    // instantiate a loader
    const loader = new THREE.TextureLoader();
    // load a resource
    loader.load(
      // resource URL
      '/solar.jpg',

      // onLoad callback
      function ( texture ) {
        // in this example we create the material when the texture is loaded
        texture.encoding = THREE.sRGBEncoding;
        const skyboxMat = new THREE.MeshBasicMaterial( {
          map: texture
        } );
            skyboxMat.side = THREE.BackSide;
            let skyboxGeom = new THREE.SphereGeometry(3000, 128, 128);
            let skybox = new THREE.Mesh(skyboxGeom, skyboxMat);
            skybox.position.set(0,0,0);
            skybox.name = 'skyBox';
            scene.add(skybox);
      },

      // onProgress callback currently not supported
      undefined,

      // onError callback
      function ( err ) {
        console.error( 'An error happened.' );
      }
    );

    //ENVIRONMENT create starfield
    const starfield = new Stars(500,2500,500);
    scene.add(starfield.starfield);

    //Wavefield
    let waveGeometry = new THREE.PlaneGeometry(512, 512, 512, 512);
    const waveMaterial = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: vertexshader(),
        fragmentShader: fragmentshader()
    });
    // const material = new THREE.MeshStandardMaterial();
    let waveMesh = new THREE.Mesh(waveGeometry, waveMaterial);
    let pointMesh = new THREE.Points(waveGeometry, waveMaterial);
    waveMesh.name = 'waveMesh';
    pointMesh.name = 'pointMesh';
    scene.add(waveMesh);
    scene.add(pointMesh);
    // Rotation anbd position
    waveMesh.rotation.x = 3.1415 / 2;
    waveMesh.position.set(0,-10,0);
    pointMesh.rotation.x = 3.1415 / 2;
    pointMesh.position.set(0,-10.1,0);

    //LIGHT
    const lightA = new THREE.PointLight(0xffffff, 1, 3000, 3);
    const lightB = new THREE.PointLight(0xffffff, 1, 3000, 3);
    lightA.position.set(0,10,10);
    lightB.position.set(0,-10,-10);
    scene.add( lightA, lightB );

    const renderer = new THREE.WebGLRenderer( { antialias: true } );
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.setSize( window.innerWidth, window.innerHeight );
    renderer.setAnimationLoop( animation );
    renderer.compile(scene, camera);
    //document.body.appendChild( renderer.domElement );
    const controls = new OrbitControls( camera, renderer.domElement );
    controls.maxDistance = 70;

    //get Canvas for WebGL usage
    //const canvRef = document.getElementById("canv");
    //document.body.innerHTML = '';
    document.body.appendChild(renderer.domElement);

    //Report Mouse Position
    var mousePos = new THREE.Vector3();
    var travelling = false;
    var travel;

    function interactionEvent (event) {
      event.preventDefault();
      var localX;
      var localY;
      if (event.type === 'click') {
        localX = -1+((event.clientX - event.target.offsetLeft)/window.innerWidth)*2;
        localY = 1-((event.clientY - event.target.offsetTop)/window.innerHeight)*2;
      } else if (event.type === 'touchstart') {
        localX = -1+((event.touches[0].pageX - event.target.offsetLeft)/window.innerWidth)*2;
        localY = 1-((event.touches[0].pageY - event.target.offsetTop)/window.innerHeight)*2;
      } else {
        return;
      }
      mousePos = { x: localX, y: localY, z: 0 };
      //Find Intersections
      let ret = findIntersections(mousePos, camera, dataObjects, dataObjectsAmount, scene);
      if ((ret !== -1 || pyHover !== -1) && !travelling) {
          ret = ret !== -1? ret : pyHover;
          travel = new Travel(camera,dataObjects[ret].pyAssembly,ret,controls);
          if (travel.trStepsCount !== -1) {
              travelling = true;
              controls.enabled = false;
          }
      }; 
    }

    //Check intersection on touchstart
    window.addEventListener('touchstart', (event) => {
      interactionEvent(event);
    });

    //Check intersection on mouse click
    window.addEventListener('click', (event) => {
      interactionEvent(event);
    });

    //Reset timer on mouse move
    window.addEventListener('mousemove', (event) => {
        timerSet = Date.now();
        globalMouseEvent = event;
        globalMouseEventHappened = true;
    })

    //Reset timer on touch event
    window.addEventListener('touchstart', (event) => {
      timerSet = Date.now();
      globalMouseEvent = event;
      globalMouseEventHappened = true;
  })

    window.addEventListener('resize', ()=>{
      renderer.setSize(window.innerWidth, window.innerHeight);
      camera.aspect = window.innerWidth/window.innerHeight;
      camera.updateProjectionMatrix();
    })

    ////////////
    //
    // ANIMATION
    //
    ////////////

    //ANIMATION PREP
    var actedOnFocus = true;
    var focusResult;
    var oldFocusResultId;

    function animation( time ) {

        //////
        //check dataset and changes
        //////
        // process data if data available
        if (dataAq.dataSet != null && dataAq.dataSet.length > 0) {
          dataAq.processData();
        }
        // look every 500 milliseconds for changes
        if ((Date.now()-timerData) > 500) {
          if (actedOnFocus) { //if there is no more focus action pending
            oldFocusResultId = focusResult?._id > ''? focusResult._id : oldFocusResultId; // copy last focus Result to old to preserve _id in case focusResult._id has a valid value
            focusResult = null; // clean 
            focusResult = dataAq.hasFocus(); // look for new focus result from data 
            if (focusResult !== null) { // if exists then signal that focus action is pending
              actedOnFocus = false;
            }
          }
          //check for necessary color changes
          if (!actedOnFocus && focusResult.last === 7) { //if the last focus update is color change
            if (oldFocusResultId != null && oldFocusResultId === focusResult._id) { // and the color change has same id as last focus
              for (let index = 0; index < dataObjects.length; index++) {
                if (dataObjects[index].pyAddition) { //look through the dataObjects if a pyAdditionObject exists and update their colors
                  dataObjects[index].pyAdditionObject.material.color = new THREE.Color(`#${focusResult.color}`);
                  dataObjects[index].pyAdditionObject.material.emissive = new THREE.Color(`#${focusResult.color}`);
                }
              }
            }
            actedOnFocus = true;
          }
          //check for open dataObject that is subject to latest focus
          if (!actedOnFocus && focusResult.last < dataObjects.length && dataObjects[focusResult.last].pyOpenState) {
            dataObjects[focusResult.last].overWrite(focusResult, camera, scene);
            timerSet = Date.now(); //Reset timer, so we still stay at object
            actedOnFocus = true;
          }
          //get new Data from server
          dataAq.queryData();
          timerData = Date.now()

          //tutorial timer
          introduction.checkTimer();
        }
        //Update datasets in dataObjects
        if (dataAq.hasUpdate) {
          dataObjects.forEach(element => {
            element.updateScore(dataAq.questionsAndAnswersValues[element.pyIndex]);
          });
          dataAq.hasUpdate = false;
        }

        //WAVEMATERIAL animation
        waveMaterial.uniforms.u_time.value += 0.05;
        waveMaterial.uniforms.u_time_frag.value += 0.002;

        //PYRAMIDES Animate material colors of Pyramids and lights alongside waveform
        let pyColor = new THREE.Color(Math.abs(Math.sin(waveMaterial.uniforms.u_time_frag.value))*0.5,.4,.4);
        let pyColorDamped = new THREE.Color(Math.abs(Math.sin(waveMaterial.uniforms.u_time_frag.value))*0.1,.05,.05);
        lightB.color = pyColor;
        for (let i = 0; i < dataObjects.length; i++) {
            dataObjects[i].animate(time, 10000, pyColor, camera);
            dataObjects[i].checkDistance(camera, 40);          
        }
        //STARFIELD Animate color of starfield
        starfield.animate(pyColorDamped);
        starfield.starfield.getObjectByName('starfieldCollector').instanceColor.needsUpdate = true;

        //CAMERA TRAVEL
        if (travelling) {
          if (travel.travel(camera, controls) === 1) {
            travelling = false;
            controls.target = dataObjects[travel.trObjectLastTraveled].pyAssembly.position;
            controls.enabled = true;
          }
        }

        //check mouse over
        // has to happen after pyramides animation since it overrides the not showing anything for Scoring blobs
        if (globalMouseEventHappened) {
            const localX = -1+((globalMouseEvent.clientX - globalMouseEvent.target.offsetLeft)/window.innerWidth)*2;
            const localY = 1-((globalMouseEvent.clientY - globalMouseEvent.target.offsetTop)/window.innerHeight)*2;
            mousePos = { x: localX, y: localY, z: 0 };
            //Find Intersections
            let ret = findIntersectionsHover(mousePos, camera, dataObjects, dataObjectsAmount, scene);
            if (ret[0] !== -1 && ret[2] === 'blob') {
                dataObjects[ret[0]].exposeScoreBlob(ret[1]);
            };
            if (ret[0] !== -1 && ret[2] === 'py' && !dataObjects[ret[0]].pyOpenState) {
              let pyDescriptor = dataObjects[ret[0]].pyContent[0];
              pyDiv.innerHTML = pyDescriptor;
              pyDiv.style.left = `${globalMouseEvent.clientX}px`;
              pyDiv.style.top = `${globalMouseEvent.clientY}px`;
              pyDiv.style.visibility = 'visible';
              pyHover = ret[0];
            } else {
              if (pyHover === -1) pyDiv.style.visibility = 'hidden';
              if (pyHover !== -1) pyHover = -1;
            }; 
        }

        //check timer for animation
        if ((Date.now()-timerSet) > 15000) {
            if (!travelling) {
                if (focusResult != null && focusResult.last <= 6 && !actedOnFocus) { //if there is a focusObject and it is not color-focussed
                  lastPyramide = focusResult.last; //take over last changed for travel itenary
                  dataObjects[focusResult.last].simulateAddition(focusResult);
                  actedOnFocus = true;
                } else {
                  if (travel instanceof Travel) {
                      lastPyramide = travel.trObjectLastTraveled + 1;
                  } else {
                      lastPyramide += 1;
                  }
                  if (lastPyramide >= dataObjectsAmount || isNaN(lastPyramide)) {
                      lastPyramide = 0;
                  }
                }
                travel = new Travel(camera,dataObjects[lastPyramide].pyAssembly,lastPyramide,controls);
                if (travel.trStepsCount !== -1) {
                    travelling = true;
                    controls.enabled = false;
                }
            };
            timerSet = Date.now();
        }

        controls.update();

      renderer.render( scene, camera );

    }

    return (
      null
    )
  }
}

export default Visualizer